/**
 * **MT-17 — match a ClickUp task to a post by hand.** Admin only.
 *
 * Jesse, 6 Oct: *"a way to manually add ClickUp Ids… I'm fine doing this
 * manually, but is there a place to do that?"*
 *
 * **One box, three ways in** (Nilay, 7 Oct — pasting must stay, add easier
 * ways):
 * 1. **Suggestions before typing** — same channel and type, ±3 days, the tasks
 *    the automatic rule weighed first. Usually the right one is here.
 * 2. **Type to search** by episode name or number, with an All Channels toggle.
 * 3. **Paste a task id or ClickUp link**, from any channel.
 *
 * Choosing a task shows **one confirm step that warns but never blocks** — a
 * task filed under another channel or type, or one another post holds
 * automatically (that post loses it). A task another post holds **by pin** is
 * shown greyed with who holds it: one task, one post.
 *
 * Saving does not change the page at once. The web container cannot write
 * ClickUp's tables, so the worker applies the pin on its next poll — the
 * message says *within a minute* and the row says *Applying* until it has.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  api,
  type MatchAssetType,
  type MatchSuggestion,
} from '../lib/api'
import { formatDate } from '../lib/dates'
import { useSession } from '../lib/session'
import { Button, Note, Skeleton, Tag } from './ui'

const TYPE_NAMES: Record<string, string> = {
  STORY: 'Story',
  SPOTLIGHT: 'Spotlight',
  SAVED_STORY: 'Saved Story',
}

/** What a row needs to offer, and show, a manual match. */
export interface MatchTarget {
  asset_id: string
  asset_type: MatchAssetType
  display_label: string
  published_at: string | null
  chosen_task_id?: string | null
  match_source?: 'automatic' | 'manual' | 'linked' | null
  pinned_task_id?: string | null
  cross_channel?: boolean
}

/**
 * The row's tags, for everyone, and the Admin's button.
 *
 * *Applying* covers the gap between saving and the worker's next poll: a pin
 * that differs from what the page shows, or a removal not yet applied.
 */
export function ManualMatchControl({ target }: { target: MatchTarget }) {
  const { me } = useSession()
  const [open, setOpen] = useState(false)
  const manual = target.match_source === 'manual'
  const pinned = target.pinned_task_id ?? null
  const applying = pinned ? !(manual && target.chosen_task_id === pinned) : manual

  return (
    // Its own line under the title: inline, the tag and link ran past the cell
    // into Publish Date, because table cells here do not wrap (seen 7 Oct).
    <span className="mt-0.5 flex flex-wrap items-center gap-1">
      {applying ? (
        // Applied by the worker on its next poll (~30s). If the worker is down —
        // 28 Sep happened — this would sit here, so it says what to check.
        <span title="Waiting for the Next ClickUp Match Run, Usually Under a Minute. If It Stays, Check Connections.">
          <Tag tone="warning">Applying…</Tag>
        </span>
      ) : manual ? (
        <Tag tone="brand">Set Manually{target.cross_channel ? ' · Cross-Channel' : ''}</Tag>
      ) : target.match_source === 'linked' ? (
        // ST-28: a saved story titled through the public story it was saved
        // from — the same media files, so this is not a date guess.
        <span title="Taken From the Public Story This Was Saved From — Its Snaps Are the Same Videos.">
          <Tag>From Public Story</Tag>
        </span>
      ) : null}
      {me?.role === 'admin' && (
        <>
          <button
            type="button"
            className="views-open text-xs"
            style={{ color: 'var(--brand-ink)' }}
            onClick={() => setOpen(true)}
            title="Choose the ClickUp Task for This Post"
          >
            {target.chosen_task_id || pinned ? 'Change Task' : 'Set Task'}
          </button>
          {open && <ManualMatchPanel target={target} onClose={() => setOpen(false)} />}
        </>
      )}
    </span>
  )
}

export function ManualMatchPanel({ target, onClose }: { target: MatchTarget; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [allChannels, setAllChannels] = useState(false)
  const [picked, setPicked] = useState<MatchSuggestion | null>(null)
  const [message, setMessage] = useState<{ tone: 'good' | 'error'; text: string } | null>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal()
    } else {
      dialog.setAttribute('open', '')
    }
    input.current?.focus()
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250)
    return () => clearTimeout(timer)
  }, [term])

  const list = useQuery({
    queryKey: ['match-suggestions', target.asset_id, debounced, allChannels],
    queryFn: () => api.matchSuggestions(target.asset_id, debounced, allChannels),
  })

  /** The worker polls every ~30s; refresh the tables once it has had time. */
  const refreshSoon = (seconds: number) => {
    window.setTimeout(() => {
      void queryClient.invalidateQueries()
    }, (seconds + 10) * 1000)
    void queryClient.invalidateQueries()
  }

  const save = useMutation({
    mutationFn: (taskId: string) => api.setManualMatch(target.asset_id, taskId),
    onSuccess: (result) => {
      setMessage({ tone: 'good', text: result.detail })
      setPicked(null)
      refreshSoon(result.starts_within_seconds)
    },
    onError: (error: Error) => setMessage({ tone: 'error', text: error.message }),
  })
  const remove = useMutation({
    mutationFn: () => api.removeManualMatch(target.asset_id),
    onSuccess: (result) => {
      setMessage({ tone: 'good', text: 'Manual match removed. This post will go back to its automatic match in about a minute.' })
      refreshSoon(result.starts_within_seconds)
    },
    onError: (error: Error) => setMessage({ tone: 'error', text: error.message }),
  })

  const close = () => {
    ref.current?.close?.()
    onClose()
  }

  const data = list.data
  const searching = debounced.length > 0 && data?.mode === 'search'

  return (
    <dialog
      ref={ref}
      className="panel-dialog"
      aria-label="Set ClickUp Task"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === ref.current) close()
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs" style={{ color: 'var(--text-label)' }}>
            Set ClickUp Task · {TYPE_NAMES[target.asset_type] ?? target.asset_type}
          </div>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            {target.display_label} · Published {formatDate(target.published_at)}
          </h2>
          {data?.pinned_task_id && (
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Matched Manually.{' '}
              <button
                type="button"
                className="views-open"
                style={{ color: 'var(--status-critical)' }}
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                Remove Manual Match
              </button>
            </div>
          )}
        </div>
        <button type="button" className="lightbox-close-inline" aria-label="Close" onClick={close}>
          ×
        </button>
      </div>

      {/* Hidden once a task is picked: typing would silently drop the choice
          being confirmed (Nilay, 7 Oct). Back brings it back. */}
      {!picked && (
        <>
      <label className="mt-3 block text-xs" style={{ color: 'var(--text-label)' }}>
        Search by Episode Name or #, or Paste a ClickUp Task ID or Link
        <input
          ref={input}
          type="search"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value)
            setPicked(null)
            setMessage(null)
          }}
          className="mt-1 block w-full px-2 py-1.5 text-sm"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
          }}
          placeholder="e.g. Movie Date, 437, or app.clickup.com/t/86ajvgxpf"
        />
      </label>
      {(searching || allChannels) && (
        <label className="mt-1 inline-flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            checked={allChannels}
            onChange={(event) => setAllChannels(event.target.checked)}
          />
          All Channels
        </label>
      )}

        </>
      )}
      {message && (
        <div className="mt-2">
          <Note tone={message.tone}>{message.text}</Note>
        </div>
      )}

      {picked ? (
        <Confirm
          task={picked}
          target={target}
          saving={save.isPending}
          onBack={() => setPicked(null)}
          onConfirm={() => save.mutate(picked.task_id)}
        />
      ) : list.isLoading ? (
        <Skeleton rows={4} />
      ) : list.error ? (
        <Note tone="error">{(list.error as Error).message}</Note>
      ) : data?.not_found ? (
        <Note>Task Not Found. New Tasks Appear After the Nightly Sync.</Note>
      ) : !data || data.rows.length === 0 ? (
        <Note>{debounced ? 'No Matching Tasks on This Channel.' : 'No Tasks Near This Date on This Channel. Try Searching.'}</Note>
      ) : (
        <ul className="mt-2" role="listbox" aria-label="ClickUp Tasks">
          {data.mode === 'suggested' && (
            <li className="mb-1 text-xs" style={{ color: 'var(--text-label)' }}>
              Suggested — Same Channel, Within 3 Days
            </li>
          )}
          {data.rows.map((task) => (
            <TaskOption
              key={task.task_id}
              task={task}
              current={task.task_id === (data.pinned_task_id ?? data.current_task_id)}
              onPick={() => setPicked(task)}
            />
          ))}
        </ul>
      )}
    </dialog>
  )
}

function dayLabel(offset: number | null): string | null {
  if (offset === null) return null
  if (offset === 0) return 'Same Day'
  const days = Math.abs(offset)
  return `${days} Day${days === 1 ? '' : 's'} ${offset > 0 ? 'After' : 'Before'}`
}

function TaskOption({
  task,
  current,
  onPick,
}: {
  task: MatchSuggestion
  current: boolean
  onPick: () => void
}) {
  const locked = task.held_by?.source === 'manual'
  // 7 Oct: "Matched Automatically to…" was a grey third line, easy to read past.
  // A task in use now says so on its title line *and* down its left edge —
  // amber where choosing it takes it from another post, grey where it cannot
  // be chosen at all.
  const inUse = task.held_by !== null
  const edge = locked ? 'var(--border-strong)' : inUse ? 'var(--status-warning)' : 'var(--border)'
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={current}
        aria-disabled={locked}
        disabled={locked || current}
        onClick={onPick}
        className="mb-1 block w-full px-2 py-1.5 text-left text-sm"
        style={{
          border: '1px solid var(--border)',
          borderLeft: `3px solid ${edge}`,
          borderRadius: 'var(--radius-md)',
          background: current ? 'var(--brand-tint)' : 'var(--bg-surface)',
          color: 'var(--text-primary)',
          opacity: locked ? 0.55 : 1,
          cursor: locked || current ? 'default' : 'pointer',
        }}
      >
        <div className="flex flex-wrap items-center gap-1">
          <span className="font-medium">{task.name ?? task.task_id}</span>
          {current && <Tag tone="brand">Current</Tag>}
          {task.held_by && !current && (
            <Tag tone={locked ? 'neutral' : 'warning'}>
              {locked ? 'Matched Manually Elsewhere' : 'In Use · Matched Automatically'}
            </Tag>
          )}
          {task.considered && <Tag>Considered by the Rule</Tag>}
          {task.cross_channel && <Tag tone="warning">{task.display_label ?? 'Other Channel'}</Tag>}
          {task.cross_type && task.intended_asset_type && (
            <Tag tone="warning">{TYPE_NAMES[task.intended_asset_type] ?? task.intended_asset_type} Task</Tag>
          )}
        </div>
        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          {[
            task.episode_number && `Ep. ${task.episode_number}`,
            task.author,
            task.publish_date && formatDate(task.publish_date),
            dayLabel(task.day_offset),
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {task.held_by && (
          <div className="text-xs" style={{ color: locked ? 'var(--text-label)' : 'var(--text-secondary)' }}>
            {locked
              ? `Already Matched to the ${task.held_by.display_label} Post of ${formatDate(task.held_by.published)}.`
              : `Matched Automatically to the ${task.held_by.display_label} Post of ${formatDate(task.held_by.published)} — Choosing It Moves It Here.`}
          </div>
        )}
      </button>
    </li>
  )
}

function Confirm({
  task,
  target,
  saving,
  onBack,
  onConfirm,
}: {
  task: MatchSuggestion
  target: MatchTarget
  saving: boolean
  onBack: () => void
  onConfirm: () => void
}) {
  const warnings = [
    task.cross_channel &&
      `This Task Is Filed Under ${task.display_label ?? 'Another Channel'}. Match It to This ${target.display_label} Post?`,
    task.cross_type &&
      task.intended_asset_type &&
      `This Is a ${TYPE_NAMES[task.intended_asset_type] ?? task.intended_asset_type} Task, and This Post Is a ${TYPE_NAMES[target.asset_type]}.`,
    task.held_by &&
      `The ${task.held_by.display_label} Post of ${formatDate(task.held_by.published)} Holds This Task Automatically and Will Lose It.`,
  ].filter(Boolean) as string[]

  return (
    <div className="mt-3">
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
        Match <strong>{task.name ?? task.task_id}</strong>
        {task.episode_number ? ` (Ep. ${task.episode_number})` : ''} to This Post?
      </div>
      {warnings.map((warning) => (
        <div key={warning} className="mt-2">
          <Note tone="warning">{warning}</Note>
        </div>
      ))}
      <div className="mt-3 flex gap-2">
        <Button variant="primary" onClick={onConfirm} disabled={saving}>
          {saving ? 'Saving…' : 'Confirm'}
        </Button>
        <Button onClick={onBack}>Back</Button>
      </div>
    </div>
  )
}
